<template lang="pug">
v-container
  v-row
    v-col(cols="12")
      h1 Настройки проектов
    v-col(cols="12")
      core-node(
        @edit="editProject"
        @open-ai="openAiSettings"
        @open-ai-progress="openAiProgress"
        @delete="deleteProject"
        @open-create="dialog = true"
        add-title="Добавить проект"
        :loading="false"
        :show-ai-button="true"
        :ai-status-by-id="aiStatusByProjectId"
        :items="projects"
      )
        template(#children="{ item, selected }")
          project-roles(
            v-if="selected === item.id"
            :project-id="selected"
          )

  v-dialog(v-model='dialog' max-width='500px' dark)
    v-card.pa-5
      p.text-h5 {{ title }}
      v-text-field(
        required
        v-model="form.name" filled label='Название проекта')
      v-btn.mr-4(
        :disabled="!form.name"
        color="success"
        @click="saveProject"
      ) Сохранить
      v-btn(text @click='dialog = false') Отменить

  v-dialog(v-model="aiDialog" max-width="700px" dark)
    v-card.pa-5
      p.text-h5 Настройки AI проекта
      v-text-field(
        v-model="aiForm.name"
        filled
        label="Name"
        :disabled="aiLoading"
      )
      v-text-field(
        v-model="aiForm.description"
        filled
        label="Description"
        :disabled="aiLoading"
      )
      v-text-field(
        v-model="aiForm.prompt_id"
        filled
        label="Prompt ID"
        :disabled="aiLoading"
      )
      v-text-field(
        v-model="aiForm.token"
        filled
        label="Token"
        :disabled="aiLoading"
      )
      v-text-field(
        v-model="aiForm.open_ai_project_id"
        filled
        label="OpenAI Project ID"
        :disabled="aiLoading"
      )
      v-text-field(
        v-model="aiForm.vector_store_id"
        filled
        label="Vector Store ID"
        :disabled="aiLoading"
      )
      input(type="hidden" v-model="aiForm.project_id")
      v-btn.mr-4(
        color="success"
        :loading="aiSaving"
        :disabled="!canSaveAiForm || aiLoading"
        @click="saveAiSettings"
      ) Сохранить
      v-btn(text @click="aiDialog = false" :disabled="aiSaving") Отменить

  v-dialog(v-model="aiProgressDialog" max-width="520px" dark)
    v-card.pa-5
      p.text-h5 Прогресс базы знаний
      v-progress-linear(
        v-if="aiProgressLoading"
        indeterminate
        color="primary"
        class="mb-4"
      )
      template(v-else)
        p.mb-2
          b Проект:
          |  {{ aiProgressProjectName || '-' }}
        p.mb-2
          b Успешно:
          |  {{ aiProgressStats.success_text }}
        p.mb-2
          b Всего файлов:
          |  {{ aiProgressStats.total }}
        p.mb-2
          b Не выгружено:
          |  {{ aiProgressStats.not_completed }}
        p.mb-2
          b В обработке:
          |  {{ aiProgressStats.in_progress }}
        p.mb-2
          b Ошибка:
          |  {{ aiProgressStats.failed }}
        p.mb-0
          b Новые:
          |  {{ aiProgressStats.new }}
      v-btn.mt-4(text @click="aiProgressDialog = false" :disabled="aiProgressLoading") Закрыть

  smart-dialog(
    title="Действительно удалить проект?"
    ref="deleteProjectPopup"
    type="error"
  )

</template>

<script>
import projectService, { CRUDProjects } from '@/services/API/project';
import ProjectRoles from './components/ProjectRoles.vue';
import CoreNode from './components/CoreNode.vue';
import SmartDialog from '@/components/dialogs/SmartDialog.vue';

const DEFAULT_AI_FORM = {
  name: '',
  description: '',
  prompt_id: '',
  token: '',
  open_ai_project_id: '',
  vector_store_id: '',
  project_id: null,
};

const AI_REQUIRED_FIELDS = [
  'name',
  'description',
  'prompt_id',
  'token',
  'open_ai_project_id',
  'vector_store_id',
];

export default {
  components: { ProjectRoles, CoreNode, SmartDialog },
  data() {
    return {
      projects: [],
      dialog: false,
      aiDialog: false,
      aiLoading: false,
      aiSaving: false,
      aiProgressDialog: false,
      aiProgressLoading: false,
      aiProgressProjectName: '',
      aiProgressStats: {
        total: 0,
        completed: 0,
        failed: 0,
        in_progress: 0,
        new: 0,
        not_completed: 0,
        success_text: '0 из 0',
      },
      aiStatusByProjectId: {},

      form: {
        name: '',
      },
      aiForm: { ...DEFAULT_AI_FORM },

    };
  },
  computed: {
    title() {
      return this.form.id ? 'Изменение проекта' : 'Создание проекта';
    },
    canSaveAiForm() {
      return !!this.aiForm.name
        && !!this.aiForm.description
        && !!this.aiForm.prompt_id
        && !!this.aiForm.token
        && !!this.aiForm.open_ai_project_id
        && !!this.aiForm.vector_store_id
        && !!this.aiForm.project_id;
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      const params = {
        'per-page': -1,
        'sort': '-id',
      };

      try {
        const response = await CRUDProjects.getAll(params);
        this.projects = response.data.data;
        await this.checkProjectsAiStatus();
      } catch (error) {
        this.$toast.error(error.response.data.result_message);
      }
    },

    isAiDataComplete(aiData) {
      if (!aiData) return false;

      return AI_REQUIRED_FIELDS.every((field) => {
        const value = aiData[field];
        return typeof value === 'string' && value.trim().length > 0;
      });
    },

    async checkProjectsAiStatus() {
      const statusMap = {};

      this.projects.forEach(({ id }) => {
        statusMap[id] = 'loading';
      });
      this.aiStatusByProjectId = statusMap;

      await Promise.all(this.projects.map(async ({ id }) => {
        try {
          const response = await projectService.ai(id);
          const aiData = response?.data?.data;
          statusMap[id] = this.isAiDataComplete(aiData) ? 'complete' : 'empty';
        } catch (error) {
          statusMap[id] = 'error';
        }
      }));

      this.aiStatusByProjectId = { ...statusMap };
    },

    async deleteProject(id) {
      const result = await this.$refs.deleteProjectPopup.open();

      if (!result) return;

      try {
        this.deleteProjectDialog = false;
        await CRUDProjects.delete(id);
        this.projects = this.projects.filter(({ id :projectId }) => projectId !== id);
      } catch (error) {
        this.$toast.error(error.response.data.result_message);
      }
    },

    editProject(data) {
      this.form = { ...data };
      this.dialog = true;
    },

    async openAiSettings(project) {
      this.aiDialog = true;
      this.aiLoading = true;
      this.aiForm = {
        ...DEFAULT_AI_FORM,
        project_id: project.id,
      };

      try {
        const response = await projectService.ai(project.id);
        const aiData = response?.data?.data;
        this.aiStatusByProjectId = {
          ...this.aiStatusByProjectId,
          [project.id]: this.isAiDataComplete(aiData) ? 'complete' : 'empty',
        };

        if (aiData) {
          this.aiForm = {
            name: aiData.name || '',
            description: aiData.description || '',
            prompt_id: aiData.prompt_id || '',
            token: aiData.token || '',
            open_ai_project_id: aiData.open_ai_project_id || '',
            vector_store_id: aiData.vector_store_id || '',
            project_id: project.id,
          };
        }
      } catch (error) {
        this.$toast.error(error.response?.data?.result_message || 'Не удалось получить настройки AI');
      } finally {
        this.aiLoading = false;
      }
    },

    async openAiProgress(project) {
      this.aiProgressDialog = true;
      this.aiProgressLoading = true;
      this.aiProgressProjectName = project.name;
      this.aiProgressStats = {
        total: 0,
        completed: 0,
        failed: 0,
        in_progress: 0,
        new: 0,
        not_completed: 0,
        success_text: '0 из 0',
      };

      try {
        const response = await projectService.aiProgress(project.id);
        this.aiProgressStats = {
          ...this.aiProgressStats,
          ...(response?.data?.data || {}),
        };
      } catch (error) {
        this.$toast.error(error.response?.data?.result_message || 'Не удалось получить прогресс базы знаний');
      } finally {
        this.aiProgressLoading = false;
      }
    },

    async saveAiSettings() {
      this.aiSaving = true;

      try {
        await projectService.saveAi(this.aiForm.project_id, this.aiForm);
        this.aiStatusByProjectId = {
          ...this.aiStatusByProjectId,
          [this.aiForm.project_id]: 'complete',
        };
        this.$toast.success('Настройки AI успешно сохранены');
        this.aiDialog = false;
      } catch (error) {
        this.$toast.error(error.response?.data?.result_message || 'Не удалось сохранить настройки AI');
      } finally {
        this.aiSaving = false;
      }
    },

    async saveProject() {
      this.dialog = false;

      if (this.form.id) {
        const response = await CRUDProjects.update(this.form.id, this.form);
        const index = this.projects.findIndex(({ id }) => id === this.form.id);
        const newProjects = [...this.projects];
        newProjects[index] = response.data.data;
        this.projects = newProjects;
      } else {
        const response = await CRUDProjects.create(this.form);
        this.projects.unshift(response.data.data);
      }
      this.form = { name: '' };
    },
  },
};
</script>
